/**
 * Whitepaper — long-form marketing/thought-leadership page.
 *
 * Route: /resources/cost-of-cash-pay-programs
 *
 * Nodera-authored perspective on the true operating cost of running
 * consumer-centric cash-pay programs when the clinic outsources vendors.
 * Ends with a compression story + demo CTA.
 */
import { Link } from "react-router-dom";
import { Section } from "@/components/Section";
import { ArrowLeft, ArrowRight, Clock, Download, FileText } from "lucide-react";
import { usePageMeta } from "@/lib/usePageMeta";
import { findWhitepaper } from "@/data/whitepapers";

const HEADLINE_TABLE = [
  { label: "Program design & offer construction", hours: "60–120", cost: "$5,700 – $11,400" },
  { label: "Vendor sourcing & vetting", hours: "40–80", cost: "$3,800 – $7,600" },
  { label: "Workflow design & documentation", hours: "40–80", cost: "$3,800 – $7,600" },
  { label: "Vendor onboarding & integration", hours: "30–60", cost: "$2,850 – $5,700" },
  { label: "Ongoing vendor management (annualized)", hours: "100–200", cost: "$9,500 – $19,000" },
  { label: "Consumer marketing (planning + weekly ops)", hours: "200–400", cost: "$19,000 – $38,000" },
  { label: "Patient concierge / coordination (annualized)", hours: "400–800", cost: "$38,000 – $76,000" },
  { label: "Compliance, SOPs, audit trail", hours: "30–60", cost: "$2,850 – $5,700" },
];

const THREE_PROGRAM_TABLE = [
  { line: "Program design", one: "$8,500", three: "$22,000" },
  { line: "Vendor sourcing", one: "$5,700", three: "$14,000" },
  { line: "Workflow design", one: "$5,700", three: "$14,000" },
  { line: "Vendor onboarding", one: "$4,300", three: "$11,000" },
  { line: "Ongoing vendor mgmt", one: "$14,000", three: "$32,000" },
  { line: "Consumer marketing", one: "$28,000", three: "$75,000" },
  { line: "Patient coordination", one: "$57,000", three: "$145,000" },
  { line: "Compliance & SOPs", one: "$4,300", three: "$9,500" },
];

const COMPRESSION_TABLE = [
  { item: "Program design", inhouse: "60–120 hrs", nodera: "4–8 hrs (template activation)" },
  { item: "Vendor sourcing", inhouse: "40–80 hrs", nodera: "0 (pre-vetted network)" },
  { item: "Vendor onboarding", inhouse: "30–60 hrs", nodera: "2–4 hrs (SSO + branding)" },
  { item: "Workflow design", inhouse: "40–80 hrs", nodera: "0 (turnkey, versioned)" },
  { item: "Workflow management", inhouse: "60–120 hrs/yr", nodera: "Managed by the platform" },
  { item: "Vendor management", inhouse: "100–200 hrs/yr", nodera: "20–40 hrs/yr (escalations only)" },
  { item: "Consumer marketing", inhouse: "200–400 hrs/yr", nodera: "60–100 hrs/yr (playbooks)" },
  { item: "Compliance & SOPs", inhouse: "30–60 hrs", nodera: "Inherited (BAA registry, SOPs)" },
];

const SECTIONS = [
  {
    num: "1",
    title: "Framing — what \"consumer-centric\" actually means",
    body: [
      "A consumer-centric cash-pay program has four operational qualities that a traditional insurance line does not:",
      "**The patient is the buyer.** They compare you to a website, an at-home kit, and a competitor across town. Anything friction-y — a hard-to-schedule intake, a confusing invoice, a vendor result that arrives late — is a churn event, not just a service failure.",
      "**Vendors are a delivery layer, not a referral.** Diagnostics, lab draws, wearables, coaching, pharmacy, and compounding pharmacies are all part of your branded experience. The patient does not care that their sleep study is Itamar, or their metabolic panel is Quest, or their compounded GLP-1 comes from a 503A pharmacy. They care that you delivered a smooth outcome.",
      "**The margin is unforgiving.** Cash-pay margins look large until you subtract the labor cost of coordination. A $2,400 sleep program that consumes 6 staff hours across intake, ordering, vendor follow-up, results delivery, and 30-day follow-up has already spent $400–$600 on labor before you consider marketing.",
      "**Marketing is a permanent operating cost.** Unlike insurance-driven volume that flows through referrals, cash-pay volume decays the instant you stop feeding it. Every program needs a weekly content + funnel motion.",
      "Miss any one of these four and the program either doesn't scale or scales into a labor sinkhole.",
    ],
  },
  {
    num: "2",
    title: "Program design — 60 to 120 hours",
    body: [
      "The number people underestimate most, because it feels like a one-time upfront task.",
      "**Market and competitive research.** Understanding what similar cash-pay programs charge in your metro, what the digital-native competitors (Ro, Hims, Function Health, Lifeforce, etc.) include and exclude, and where your differentiation sits. Not a Google session — a real 15–25 hour research cycle for one category.",
      "**Consumer segmentation and positioning.** Who is this program for? A cash-pay weight-loss program that targets the 45-year-old peri-menopausal executive is a fundamentally different product than one that targets the 32-year-old post-partum patient — different intake questions, different vendors, different price points, different marketing angles.",
      "**Offer construction.** What is included at what price tier? One-shot? Recurring? Bundled? Are labs included in the base price or upsold? Is coaching monthly, weekly, on-demand? Every decision cascades into vendor cost, workflow complexity, and margin.",
      "**Pricing and margin modeling.** Backing into a defensible consumer price by starting from vendor cost stacks, staff labor, marketing CAC, and target gross margin. This is spreadsheet work but it's judgment-heavy spreadsheet work; most first drafts leave 15–25% margin on the table.",
      "**Clinical protocol design.** What are the branching decisions in the patient journey? Which inclusion/exclusion criteria disqualify a patient at intake vs. at labs vs. mid-program? Who signs what? What does the follow-up cadence look like at 30/60/90 days?",
      "**Legal and scope-of-practice review.** State-by-state constraints on telehealth, prescribing, compounding pharmacy relationships, informed-consent language.",
      "**Common failure mode:** designing the program in isolation from the vendors who will fulfill it, then discovering three weeks into vendor sourcing that the price point doesn't work, the intake criteria the clinical lead insisted on aren't compatible with the diagnostic vendor's operating model, or the marketing team cannot articulate the value prop cleanly to a consumer.",
    ],
  },
  {
    num: "3",
    title: "Vendor sourcing — 40 to 80 hours",
    body: [
      "The cash-pay economy is enabled by an ecosystem of specialized vendors — but the sourcing work sits entirely on the clinic.",
      "**Identifying candidate vendors** for each service layer (labs, diagnostics, compounding, coaching, monitoring, wearables). The public directory of \"vendors who serve cash-pay clinics\" barely exists — most sourcing happens through peer networks, industry conferences, and cold outreach.",
      "**RFI/RFP process.** For each vendor, understanding their pricing model, minimum commitments, turnaround times, data handoff format, geographic coverage, and account-management model. A short RFI is a 45-minute discovery call plus 2–3 follow-up email cycles.",
      "**Compliance and credentialing verification.** BAA execution for anyone touching PHI. State registration and licensure verification. For compounding pharmacies, 503A vs 503B distinction, USP <795>/<797> compliance posture, DEA registration. This is not optional and it is not fast.",
      "**Reference checks.** Talking to two or three clinics already using the vendor. Real conversations, not testimonials — five to seven questions each.",
      "**Contract negotiation.** Price schedules, service level agreements, termination clauses, data ownership, cross-referral restrictions. Most first-round vendor contracts contain 3–5 terms that are indefensible on a re-read; you have to catch them.",
      "**Common failure mode:** signing with the first vendor that returns a proposal. The second and third vendor conversations always change the negotiation.",
    ],
  },
  {
    num: "4",
    title: "Vendor onboarding & integration — 30 to 60 hours",
    body: [
      "Between \"signed contract\" and \"patient can flow through the vendor cleanly\" is a valley most clinics underestimate.",
      "**Account setup, SSO, permissions.** Every vendor portal has its own auth, its own permission model, its own account hierarchy. Getting the right staff members into the right vendor accounts with the right permissions takes 2–4 hours per vendor.",
      "**Data handoff design.** How does the intake form data get to the lab? How do lab results get back into your record system? Is there an actual integration or is somebody manually re-typing? Is the manual re-typing HIPAA-safe? Almost every vendor claims \"we integrate with your EHR\" and almost every integration turns out to be a shared Google Drive folder in practice.",
      "**Test patients / dry runs.** Running 2–3 fake patients through the entire flow before a real patient sees it, and finding the seams — the missing consent form, the vendor account that wasn't provisioned, the notification email that goes to the wrong inbox.",
      "**Standard operating procedures.** Writing down, for each vendor, exactly how a request gets submitted, what the expected turnaround is, what the escalation path is when it doesn't turn around, and who owns each of those steps.",
      "**Staff training.** Whoever is going to run this program needs to be able to operate the vendor tools. That's a training block, not a \"we'll figure it out on the fly.\"",
    ],
  },
  {
    num: "5",
    title: "Workflow design & documentation — 40 to 80 hours",
    body: [
      "Workflow is the connective tissue. It is what turns \"we have vendors\" into \"we have a program.\"",
      "**Patient journey mapping.** For each program, the full sequence: discovery → intake → screening → payment → diagnostic order → vendor fulfillment → result review → protocol delivery → follow-up → renewal. Every state, every branch, every \"what if\" (what if the lab is abnormal, what if the patient no-shows the intake, what if the vendor is delayed, what if the patient wants to cancel).",
      "**Event and trigger design.** Which events cause the workflow to advance? Patient submits intake form → advance. Lab result received → advance. Provider reviews and signs → advance. Payment succeeds → advance. Which events trigger notifications, which trigger tasks, which trigger escalations.",
      "**Assignment and ownership.** Every step has an owner. If the owner is \"whoever notices first,\" the step is broken. Assignment rules — by role, by patient panel, by day of the week — are unglamorous but this is where quality lives.",
      "**SLA and drift monitoring.** What's the expected turnaround at each step? At day 3 past expected turnaround, who gets alerted? At day 7? How do you notice — in aggregate — when a vendor's median turnaround has silently drifted from 3 days to 5?",
      "**Exception paths.** What happens when the vendor doesn't respond? When the patient goes silent? When labs come back abnormal in a way the protocol didn't anticipate? These need to exist before the first patient triggers them, not after.",
      "**Common failure mode:** treating workflow as a project you finish. Real programs' workflows require 2–4 iterations in the first 90 days as reality collides with the theoretical map. Budget for the rewrites.",
    ],
  },
  {
    num: "6",
    title: "Ongoing vendor management — 100 to 200 hours per year",
    body: [
      "This is the line item that surprises people the most. Vendor relationships are not \"set and forget.\"",
      "**Weekly or bi-weekly touchpoints** with your top 2–3 vendors — 30 minutes each, 25 weeks/year at minimum. Escalations, upcoming volume forecasts, product changes on their side, product changes on yours.",
      "**Quarterly business reviews (QBRs).** A serious QBR — with volume, quality, turnaround, complaint, and financial data — takes 4–6 hours of prep and 2 hours of meeting. Four per year, per top vendor.",
      "**Complaint investigation.** When a patient complains — \"the lab kit didn't arrive,\" \"the compounded medication came damaged,\" \"the vendor rescheduled my telehealth twice\" — someone has to investigate, escalate, follow up, and document. 15–30 minutes per incident, and the incident rate is not zero even with great vendors.",
      "**Contract renewals and re-negotiation.** Every 12–24 months, every vendor contract. Real preparation (comparing usage vs. commitment, benchmarking pricing, negotiating new terms) is 6–12 hours per contract.",
      "**Vendor swap-outs.** When a vendor genuinely underperforms and needs replacing — a 40–80 hour project that involves finding a replacement, running a parallel pilot, migrating patients, decommissioning the old integration.",
      "**Vendor drift monitoring.** Vendors change. Personnel turn over, integration endpoints get deprecated, service catalogs shift. Someone has to be watching, or you'll discover it via a broken patient experience.",
    ],
  },
  {
    num: "7",
    title: "Consumer marketing — 200 to 400 hours per year",
    body: [
      "For most clinics, marketing is where the \"real cost\" lives — and it's the hardest to reduce.",
      "**Positioning and messaging development.** Landing page copy, program description, differentiation language. This is a real writing task — not \"we'll paste in the vendor's blurb.\" 20–40 hours upfront per program.",
      "**Content production.** Blog posts, videos, testimonials, case studies. Even a modest cadence (2 posts/month) is 8–12 hours/month of production time.",
      "**Paid acquisition.** Facebook/Instagram ads, Google search ads, or influencer partnerships. Even an outsourced agency requires 2–4 hours/week of client-side management (creative approval, budget adjustments, campaign strategy).",
      "**Funnel design and optimization.** Landing page → screener → intake → consultation → conversion. Every drop-off point is a diagnostic problem. 4–8 hours/month of analytics + iteration.",
      "**CRM / nurture sequences.** Email flows for people who screen in but don't convert, people who converted but haven't completed intake, people mid-program who need retention. Real email sequences, not \"we'll email them when we remember.\" Setup is 20–30 hours; ongoing management is 4–6 hours/month.",
      "**Note on outsourcing:** you can absolutely outsource most of this to an agency, but agency fees ($3,000–$10,000/month per program) plus the client-side management hours (still 2–4 hours/week) usually exceed the in-house cost — just moves the dollars from labor to vendor spend.",
    ],
  },
  {
    num: "8",
    title: "Patient concierge & coordination — 400 to 800 hours per year",
    body: [
      "The unglamorous middle of every program. Even with great vendors and great workflow, patients need a human at critical moments.",
      "**Intake completion nudging.** Patients who start but don't finish the intake need reminders — the fewer, the better, but zero is a mistake.",
      "**Scheduling assistance.** Multi-vendor programs require multi-vendor scheduling. Automated is best; when it fails, human catches.",
      "**Result delivery and Q&A.** A patient who receives lab results without human context churns. Someone needs to be reachable.",
      "**Escalation triage.** Which patient complaints are the vendor's problem, which are the workflow's problem, which are the clinician's problem. Someone has to route each one.",
      "**Renewal and retention outreach.** For recurring programs (memberships, quarterly labs), the retention motion is patient-by-patient, and it's staffed.",
      "**Typical staffing model:** one FTE patient coordinator per 200–400 active patients, or one part-time coordinator per active program in year one.",
    ],
  },
  {
    num: "9",
    title: "Compliance, SOPs, and audit trail — 30 to 60 hours",
    body: [
      "Not a large line item, but a non-negotiable one.",
      "**Standard operating procedures** for every intake, vendor handoff, and follow-up. Written, versioned, dated, reviewed.",
      "**HIPAA-adjacent operational safeguards.** Access logs, minimum-necessary reviews, BAA registry, incident response plan.",
      "**Consent management.** Program-specific informed-consent forms, telehealth consent (state-by-state), consent to share data with each vendor.",
      "**Financial audit trail.** Cash-pay creates its own audit surface — receipts, refund records, chargebacks, credit-card reconciliation.",
      "**Common failure mode:** treating compliance as an event (\"we passed the audit\") rather than a maintenance cadence. State telehealth rules change. Vendor BAAs need renewal. Consent forms need to reflect current protocol.",
    ],
  },
];

// Simple inline **bold** → <strong> renderer. Kept intentionally minimal
// so this page can render the paper's prose without a full markdown lib.
function InlineBold({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

export default function Whitepaper() {
  const paper = findWhitepaper("cost-of-cash-pay-programs");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  usePageMeta({
    title: `${paper?.title || "White paper"} · Nodera Health`,
    description:
      paper?.summary ||
      "An honest accounting of the operating cost of consumer-centric cash-pay programs.",
    image: `${origin}${paper?.og_image_path || ""}`,
    url: `${origin}${paper?.public_path || ""}`,
    type: "article",
  });
  return (
    <div data-testid="whitepaper-page">
      <Section className="pt-28 pb-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 font-medium">
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </Link>

        <div className="mt-8 max-w-3xl">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
            <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> White paper</span>
            <span className="text-slate-300">•</span>
            <span className="inline-flex items-center gap-1 text-slate-500 normal-case tracking-normal font-medium">
              <Clock className="w-3 h-3" /> 14 min read
            </span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-500 normal-case tracking-normal font-medium">Feb 2026</span>
            {paper?.pdf_path && (
              <>
                <span className="text-slate-300">•</span>
                <a
                  href={paper.pdf_path}
                  download
                  data-testid="whitepaper-header-pdf-link"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 normal-case tracking-normal font-semibold"
                >
                  <Download className="w-3 h-3" /> PDF
                </a>
              </>
            )}
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
            The Real Cost of Building a Consumer-Centric Cash-Pay Program
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            An honest accounting of the time and staffing required to develop, deploy, and manage
            outsourced cash-pay programs — where the vendors deliver, and the clinic runs the operating layer.
          </p>
        </div>
      </Section>

      {/* Executive summary */}
      <Section className="pt-0 pb-16">
        <div className="max-w-3xl">
          <div className="inline-block text-[11px] uppercase tracking-[0.18em] font-bold text-blue-600 mb-3">Executive summary</div>
          <div className="space-y-5 text-slate-700 leading-[1.8] text-[17px]">
            <p>
              Cash-pay medicine is the fastest-growing profit lane in independent practice. Weight loss, sleep, hormone,
              longevity, and metabolic programs routinely produce $2,000–$15,000 in per-patient revenue at
              40–70% gross margin — numbers no insurance line can match.
            </p>
            <p>
              But the sticker price of the equipment or vendors is not what breaks these programs. What breaks them is
              <strong className="text-slate-900"> the invisible operating cost of running one well</strong>: the
              sourcing meetings, the vendor onboarding calls, the workflow rewrites after the first ten patients,
              the marketing that has to keep the funnel warm every single week, and the staff hours consumed by the
              seams between all of the above.
            </p>
            <p>
              This paper assumes you are already outsourcing the clinical, diagnostic, and product components to
              vendors — as any modern operator should. The point is to make the <em>time cost</em> of running a
              vendor-orchestrated program visible, so you can price it, staff it, or automate it deliberately.
            </p>
          </div>

          {/* Headline table */}
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="bg-slate-900 text-white px-5 py-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Fully-loaded — Year 1, single program</div>
              <div className="font-display text-lg font-bold">Headline cost stack</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="px-5 py-2.5 text-left">Cost center</th>
                  <th className="px-5 py-2.5 text-right">Hours</th>
                  <th className="px-5 py-2.5 text-right">Cost @ $95/hr blended</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {HEADLINE_TABLE.map((row) => (
                  <tr key={row.label} className="hover:bg-slate-50/60">
                    <td className="px-5 py-2.5 text-slate-700">{row.label}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-slate-600">{row.hours}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-slate-900">{row.cost}</td>
                  </tr>
                ))}
                <tr className="bg-blue-50/60 border-t-2 border-blue-200">
                  <td className="px-5 py-3 font-bold text-slate-900">Year 1 total (one program)</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">900–1,800</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">$85,500 – $171,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-8 text-slate-700 leading-[1.8] text-[17px]">
            Three concurrent programs — the point at which clinics typically start seeing meaningful cash-pay
            revenue diversification — push the annualized labor cost to <strong className="text-slate-900">$180,000–$380,000</strong>,
            most of which is not visible on the P&amp;L because it&apos;s absorbed inside existing salaries.
          </p>
        </div>
      </Section>

      {/* Body sections */}
      {SECTIONS.map((s) => (
        <Section key={s.num} className="pt-0 pb-14" testId={`whitepaper-section-${s.num}`}>
          <div className="max-w-3xl">
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-blue-600 font-bold">§{s.num}</span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
                {s.title}
              </h2>
            </div>
            <div className="space-y-5 text-slate-700 leading-[1.8] text-[17px]">
              {s.body.map((p, i) => (
                <p key={i}><InlineBold text={p} /></p>
              ))}
            </div>
          </div>
        </Section>
      ))}

      {/* Three-program table */}
      <Section className="pt-0 pb-14">
        <div className="max-w-3xl">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-blue-600 font-bold">§10</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              Bringing it together — the 3-program reality
            </h2>
          </div>
          <p className="mb-6 text-slate-700 leading-[1.8] text-[17px]">
            Most clinics we work with don&apos;t run one cash-pay program. They run three — a sleep program, a metabolic /
            weight program, and a longevity / optimization program. The Year 1 totals compound, but not linearly:
          </p>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="px-5 py-2.5 text-left">Line</th>
                  <th className="px-5 py-2.5 text-right">1 program</th>
                  <th className="px-5 py-2.5 text-right">3 programs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {THREE_PROGRAM_TABLE.map((row) => (
                  <tr key={row.line} className="hover:bg-slate-50/60">
                    <td className="px-5 py-2.5 text-slate-700">{row.line}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-slate-600">{row.one}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-slate-900">{row.three}</td>
                  </tr>
                ))}
                <tr className="bg-blue-50/60 border-t-2 border-blue-200">
                  <td className="px-5 py-3 font-bold text-slate-900">Total (Year 1)</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">$127,500</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">$322,500</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-slate-700 leading-[1.8] text-[17px]">
            Notice: <strong className="text-slate-900">patient coordination and marketing dominate the total.</strong>{" "}
            Program design and vendor sourcing — the parts that feel like the &ldquo;big projects&rdquo; — are less than 20%
            of the annual cost.
          </p>
          <p className="mt-4 text-slate-700 leading-[1.8] text-[17px]">
            <strong className="text-slate-900">What this means for your P&amp;L:</strong> if you&apos;re planning a program on
            the assumption that vendor cost + a clinician&apos;s time = your cost stack, you&apos;re understating true cost by 3–5x.
          </p>
        </div>
      </Section>

      {/* Leverage points */}
      <Section className="pt-0 pb-14">
        <div className="max-w-3xl">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-blue-600 font-bold">§11</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              Where the leverage points actually sit
            </h2>
          </div>
          <div className="space-y-5 text-slate-700 leading-[1.8] text-[17px]">
            <p>
              The temptation is to reduce these costs by cutting corners — running a program without the vendor QBR
              cadence, skipping the workflow rewrites, letting the marketing decay. That works for exactly one to two
              quarters before the program&apos;s quality (and margin) collapses.
            </p>
            <p>The leverage points that actually work:</p>
            <ol className="space-y-4 list-decimal pl-6">
              <li><strong className="text-slate-900">Turnkey program templates</strong> instead of blank-page design. Saves 40–80 hours per program and drastically reduces &ldquo;we designed it wrong&rdquo; risk.</li>
              <li><strong className="text-slate-900">Pre-vetted vendor networks</strong> instead of solo vendor sourcing. Inherits the diligence rather than repeating it.</li>
              <li><strong className="text-slate-900">Workflow-as-a-product</strong> instead of workflow-as-a-doc. Real orchestration where events advance patients, alerts fire, drift is detected — not a static SOP.</li>
              <li><strong className="text-slate-900">Marketing playbooks and positioning</strong> that transfer across markets. Compresses marketing setup from 40 hours to 8.</li>
              <li><strong className="text-slate-900">Consolidated vendor management.</strong> A single operating partner across programs reduces per-program vendor hours by 40–60%.</li>
            </ol>
            <p>None of these leverage points are exotic. They exist because someone has already done the work at scale.</p>
          </div>
        </div>
      </Section>

      {/* Compression table — Nodera positioning */}
      <Section className="pt-0 pb-14">
        <div className="max-w-3xl">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-blue-600 font-bold">§12</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              How Nodera Health compresses each line item
            </h2>
          </div>
          <p className="mb-6 text-slate-700 leading-[1.8] text-[17px]">
            We are the operating layer for consumer-centric cash-pay programs. We do not replace your clinicians,
            your license, your relationships, or your patient panel. We compress the operating cost of running
            programs well.
          </p>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="px-5 py-2.5 text-left">Line item</th>
                  <th className="px-5 py-2.5 text-left">In-house Year 1</th>
                  <th className="px-5 py-2.5 text-left">With Nodera</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COMPRESSION_TABLE.map((row) => (
                  <tr key={row.item} className="hover:bg-slate-50/60">
                    <td className="px-5 py-2.5 text-slate-700 font-medium">{row.item}</td>
                    <td className="px-5 py-2.5 text-slate-600 font-mono text-[13px]">{row.inhouse}</td>
                    <td className="px-5 py-2.5 text-blue-700 font-mono text-[13px]">{row.nodera}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-slate-700 leading-[1.8] text-[17px]">
            For a clinic running three concurrent programs, this typically maps to{" "}
            <strong className="text-slate-900">$180K–$260K of avoided Year 1 operating cost</strong>, plus the option
            value of moving faster on new programs when the market shifts.
          </p>
          <p className="mt-4 text-slate-700 leading-[1.8] text-[17px]">
            Patient coordination is the one line item that stays with you — because it&apos;s your patients, your brand
            voice, your clinical relationship. But the volume of coordination work drops by 40–60% when the workflow
            and vendor management around it are already handled.
          </p>
        </div>
      </Section>

      {/* Closing */}
      <Section className="pt-0 pb-14">
        <div className="max-w-3xl">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-blue-600 font-bold">§13</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              Closing — the honest trade
            </h2>
          </div>
          <div className="space-y-5 text-slate-700 leading-[1.8] text-[17px]">
            <p>
              Building cash-pay programs in-house is defensible if you&apos;re a large group willing to fund a dedicated
              operations team, or if your program is so specific to your practice that no external template applies.
              In those cases, use this paper as a budget guide.
            </p>
            <p>
              For everyone else — the 90% of independent practices for whom cash-pay is a growth engine but not an
              operating identity — the mathematics of hours-to-revenue argue for a different posture:{" "}
              <strong className="text-slate-900">outsource the operating layer, not the medicine</strong>.
            </p>
            <p>
              Program design, vendor sourcing, workflow orchestration, and vendor management are commoditizable in a
              way that clinical care and patient relationships are not. If somebody else can compress those line items
              by 60–80% without compromising quality, the honest question is not <em>can I do this myself?</em> — it&apos;s{" "}
              <em>should I spend Q3 running vendor RFPs, or should I spend Q3 seeing patients?</em>
            </p>
            <p>We built Nodera because we think that answer is obvious.</p>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="pt-0 pb-24">
        <div className="max-w-3xl">
          <div className="rounded-2xl bg-slate-900 text-white p-8 sm:p-10">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300 mb-2">
              See it in action
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
              Every line item in this paper is a screen in Nodera.
            </h3>
            <p className="mt-3 text-slate-300 text-[15px] leading-relaxed max-w-xl">
              A 30-minute walkthrough covers program activation, vendor coordination, workflow orchestration, and
              the ongoing management view — with your programs, not a demo dataset.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/demo"
                data-testid="whitepaper-cta-demo"
                className="inline-flex items-center gap-1.5 bg-white text-slate-900 rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-100 transition-colors"
              >
                Book a walkthrough <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              {paper?.pdf_path && (
                <a
                  href={paper.pdf_path}
                  data-testid="whitepaper-download-pdf"
                  className="inline-flex items-center gap-1.5 border border-slate-600 text-slate-100 rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors"
                  download
                >
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </a>
              )}
              <a
                href="/whitepapers/cost-of-cash-pay-programs.md"
                data-testid="whitepaper-download-md"
                className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-100 rounded-full px-3 py-2.5 text-sm font-semibold transition-colors"
                download
              >
                Markdown
              </a>
            </div>
          </div>

          {/* Appendices */}
          <div className="mt-12 space-y-8">
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Appendix A — Rate assumptions</h3>
              <p className="mt-2 text-slate-700 leading-[1.8] text-[15px]">
                All labor costs modeled at a blended <strong>$95/hour</strong> fully-loaded (salary + benefits +
                overhead). Reflects a mix of Founder/MD ($200–$400/hr), Practice manager / RN ($80–$120/hr), Patient
                coordinator / MA ($45–$65/hr), Marketing coordinator ($60–$85/hr). Substitute your own rates for a
                bespoke model — directional conclusions hold across a wide rate range.
              </p>
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Appendix B — What this paper does not cover</h3>
              <ul className="mt-2 text-slate-700 leading-[1.8] text-[15px] list-disc pl-6 space-y-1">
                <li>Capital equipment (assumed outsourced to vendors).</li>
                <li>Physical space / facility costs.</li>
                <li>EHR / practice-management software (assumed already in place).</li>
                <li>One-time state licensure or credentialing for the practice itself.</li>
                <li>Founder / owner opportunity cost of being the on-call operator during the first year — the largest unmodeled expense in this paper.</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
